import { Box } from '@mui/material';
import React from 'react';

import routes from '@/assets/constants/routes-no.ts';
import { ArrowLink } from '@/competitions/_common/components/ArrowLink.tsx';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWithConfig } from '@/lib/api/requests.ts';
import { Timer, type TimerTheme } from '@/ui/Timer.tsx';

import { MARCH_2025_CAMPAIGN_END_DATE } from '../../../config.ts';
import Chicken from '../_assets/chicken.svg?react';
import { CTA } from '../_components/CTA.tsx';
import { Section } from '../_components/Section.tsx';

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});
const dateStr = dateFormatter.format(MARCH_2025_CAMPAIGN_END_DATE);

const timerTheme: TimerTheme = {
  backgroundColor: '#031839',
  digitsColor: '#FFFFFF',
  labelsColor: '#031839',
};

export type TimerSectionProps = {
  activeCompetition: CompetitionWithConfig | undefined;
};

export const TimerSection: React.FC<TimerSectionProps> = (props) => {
  return (
    <Section
      sx={{
        gap: '20px',
        flexDirection: { md: 'row' },
        flexWrap: { md: 'wrap', xl: 'initial' },
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          textAlign: { xs: 'center', xl: 'left' },
          flex: { xl: 1.15 },
          width: { md: '100%', xl: 'auto' },
          mb: { xs: 4, md: 2 },
          pr: { xl: 8 },
          mt: { xl: 4 },
        }}
      >
        <div className="mb-6 typography-v1-h1">
          Bruk <span className="text-[#18A0FB]">gjeldvett!</span>
        </div>

        <p className="mx-auto mb-8 max-w-[70ch] typography-v1-body3">
          Vi mener at det er godt gjeldvett å sjekke om du kan få lavere rente. Lavere rente kan
          nemlig spare deg for tusenvis av kroner! Bruk gjeldvettregel nummer 3, sjekk om du kan få
          lavere rente og bli med i trekningen av å vinne nedbetaling av gjeld!
        </p>

        <div className="flex items-end gap-8">
          <CTA
            activeCompetition={props.activeCompetition}
            onClick={() => {
              sendUserPortalEvent('mar25_competition_cta_timer_clicked');
            }}
          />

          <Box sx={{ display: { xs: 'none', xl: 'block' }, flexShrink: 0 }}>
            <Chicken />
          </Box>
        </div>
      </Box>
      <div className="mx-auto flex max-w-sm flex-1 flex-col rounded-lg bg-[#EFF7FF] p-6 md:max-w-md lg:mx-0 lg:max-w-xl">
        <p className="mb-4 typography-v1-h2">Premie</p>
        <p className="mb-2 typography-v1-h3">
          Få slettet inntil{' '}
          <span className="mt-1 mb-2 block typography-v1-h1">
            <UnderlinedText>500 000 kr</UnderlinedText>
          </span>{' '}
          på din rentebærende forbruks- og kredittkortgjeld
        </p>

        <p className="mt-auto mb-4 typography-v1-body3">
          Vi nedbetaler din forbruks- og kredittkortgjeld dersom du vinner.
          <br />
          Maks. 500&nbsp;000&nbsp;kr
        </p>

        <ArrowLink
          to={routes.MARCH_2025_COMPETITION_TERMS}
          sx={{ display: 'block', my: 2, mt: { sm: 3 } }}
        >
          Vilkår og betingelser
        </ArrowLink>
      </div>
      <div className="mx-auto flex max-w-sm flex-1 flex-col rounded-lg bg-[#FFF4E0] p-6 md:max-w-md lg:mx-0 lg:max-w-xl">
        <p className="typography-v1-h2">Trekningsdato</p>
        <Box
          component="p"
          className="mb-4 typography-v1-display"
          sx={{ fontSize: { lg: '3.812rem' } }}
        >
          {dateStr}
        </Box>
        <Timer date={MARCH_2025_CAMPAIGN_END_DATE} theme={timerTheme} />

        <p className="mt-6 mb-4 typography-v1-body3 md:mt-8 xl:mt-12">
          Vinnerne kontaktes via e-post og mobil.
          <br />
          Vi tar hensyn til personvern og publiserer ikke vinnere uten samtykke.
        </p>

        <ArrowLink
          to={routes.MARCH_2025_COMPETITION_TERMS}
          sx={{ display: 'block', my: 2, mt: { sm: 3 } }}
        >
          Vilkår og betingelser
        </ArrowLink>
      </div>
    </Section>
  );
};
